import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Check } from 'lucide-react';
import { toast } from '@/components/ui/toaster.jsx';
import Panel from './Panel.jsx';
import { getQueue, clearQueueNotification, doorForItem } from '@/services/queue.service.js';
import { formatAge } from '@/routes/queue.jsx';
import { partitionQueue, waitingSummary, hasRealDoor } from '@/lib/needs-you.js';
import { clearLabel, clearTitle, DEAL_QUEUE_PARTIAL } from '@/lib/queue-labels.js';
import UsePilotValueButton from '@/components/queue/UsePilotValueButton.jsx';
import PossibleMatchButtons from '@/components/queue/PossibleMatchButtons.jsx';

// WHAT NEEDS ME (MV-10 3.2 zone 2): this deal's what's-waiting queue items,
// inline. Same derivation as the landing queue (GET /queue?deal=), same
// one-line one-action contract. On the deal itself the action opens the
// matching sheet IN PLACE (via onDoor) instead of navigating; kinds whose
// door lives on another surface (nudges, unmatched mail) still navigate.

// pilot_form_differs (R4): a held pilot value; its clearing records Keep mine
// (through its own door), and Use the pilot's sits beside it (T8e).
// name_still_needed (ruling 4, F1): a filed bind document printed a blank; cleared by hand.
// phone_intake_call: a call carries no deal today, so it reaches the agency
// queue, not this panel; listed so its Clear holds wherever it renders.
// possible_match (David 2026-10-20): a possible match raised with a deal shows here too,
// answered Same person or Not the same (PossibleMatchButtons, R6), never cleared.
// client_mail_no_address (Q26): client mail on this deal that had no address; cleared by hand.
// needs_own_email (2026-10-22 ruling): a person on this deal with no email of his own; it
// clears when his own address is written, or by hand.
// account_link_notice (account-people-door T02): a link notice raised with a deal shows here
// too; cleared by hand.
// carrier_send_held (David 2026-11-03): a carrier send on this deal the liability floor held; cleared by hand.
const NOTIFICATION_KINDS = new Set(['payment_cleared', 'signature_completed', 'phf_submitted', 'pilot_email_changed', 'typed_email_matches_client', 'pilot_form_differs', 'name_still_needed', 'phone_intake_call', 'possible_match', 'client_mail_no_address', 'needs_own_email', 'account_link_notice', 'carrier_send_held']);

// Kinds whose exact door is a sheet on this very workspace.
const LOCAL_DOOR = {
  paper_draft: 'paperwork',
  escalation: 'rtq',
  parked_intake: 'resume_link',
  quote_expiring: 'quotes',
  quote_draft: 'review_quote',
  bind_held: 'bind_hold', // decision 8: the hold sheet, in place
};

export default function DealQueuePanel({ dealId, refreshKey, onDoor }) {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [clearingId, setClearingId] = useState(null);
  const [adoptingId, setAdoptingId] = useState(null); // one decision per flag at a time (Keep mine or Use the pilot's)

  const refresh = useCallback(() => {
    if (!dealId) return;
    getQueue({ dealId })
      .then(setData)
      .catch(() => setData({ items: [], failed: true }));
  }, [dealId]);

  useEffect(() => { refresh(); }, [refresh, refreshKey]);

  async function clearNotification(item) {
    setClearingId(item.refs.notification_id);
    setData((d) => d ? { ...d, items: d.items.filter((i) => i.key !== item.key) } : d);
    try {
      await clearQueueNotification(item);
    } catch (err) {
      toast.error(err.body?.error || err.message || 'Clear failed');
    } finally {
      setClearingId(null);
      refresh();
    }
  }

  function fire(item) {
    const local = LOCAL_DOOR[item.kind];
    if (local && onDoor?.(local, item)) return;
    const door = doorForItem(item);
    if (door) navigate(door.href);
  }

  // Same split as the cockpit rail, same module — the two surfaces render the
  // same queue on the same page and must not disagree about what counts as
  // work. `waiting` collapses to one line; FYI kinds keep their Clear button
  // below the fold rather than posing as tasks.
  const all = data?.items || [];
  const { tasks, fyi } = partitionQueue(all);
  const waiting = waitingSummary(all);
  const items = [...tasks, ...fyi];
  // A source that failed or was cut (Q13): one short line; the landing queue says which.
  const source_errors = Array.isArray(data?.source_errors) ? data.source_errors : [];

  return (
    <Panel
      title="What needs me"
      count={tasks.length}
      empty={
        data === null
          ? 'Deriving…'
          : items.length === 0 && !waiting
            ? (data.failed
              ? 'Queue unavailable right now; the landing queue has the full picture.'
              : 'Nothing on this deal needs you. Reviews, follow-ups, expiring offers, and client completions surface here.')
            : null
      }
    >
      <div className="space-y-2">
        {source_errors.length > 0 && (
          <p className="px-1 text-[11px] text-warning">{DEAL_QUEUE_PARTIAL}</p>
        )}
        {items.map((item) => {
          const door = doorForItem(item);
          const isNotification = NOTIFICATION_KINDS.has(item.kind);
          return (
            <div key={item.key} className="flex flex-wrap items-center gap-x-3 gap-y-1.5 rounded-card border border-border bg-bg/40 px-3 py-2">
              <div className="min-w-0 flex-1">
                <p className="truncate text-[13px] font-medium text-text">{item.title}</p>
                <p className="text-[11px] text-text-muted">
                  {item.section === 'now' ? 'Now' : item.section === 'waiting' ? 'Waiting on others' : 'Today'}
                  {item.detail ? ` · ${item.detail}` : ''}
                  {item.age_at ? ` · ${formatAge(item.age_at)}` : ''}
                </p>
              </div>
              {hasRealDoor(item, dealId, LOCAL_DOOR) ? (
                <button
                  type="button"
                  onClick={() => fire(item)}
                  className="inline-flex min-h-[32px] shrink-0 items-center rounded-[5px] border border-border bg-surface px-2.5 py-1 text-[12px] text-text hover:border-border-strong hover:bg-surface-strong"
                >
                  {door?.label || 'Open'}
                </button>
              ) : null}
              {isNotification && item.refs?.notification_id && item.kind !== 'possible_match' ? (
                <button
                  type="button"
                  title={clearTitle(item.kind)}
                  disabled={clearingId === item.refs.notification_id || adoptingId === item.refs.notification_id}
                  onClick={() => clearNotification(item)}
                  className="inline-flex min-h-[32px] shrink-0 items-center gap-1 rounded-[5px] border border-border px-2 py-1 text-[12px] text-text-muted hover:border-border-strong hover:text-text disabled:opacity-50"
                >
                  <Check size={13} strokeWidth={1.5} /> {clearLabel(item.kind)}
                </button>
              ) : null}
              {item.kind === 'pilot_form_differs' ? <UsePilotValueButton item={item} onDone={refresh} compact disabled={clearingId === item.refs?.notification_id} onBusyChange={(b) => setAdoptingId(b ? item.refs?.notification_id : null)} /> : null}
              {item.kind === 'possible_match' ? <PossibleMatchButtons item={item} onDone={refresh} compact /> : null}
            </div>
          );
        })}
        {waiting ? (
          <p className="px-1 pt-1 text-[11px] text-text-dim">{waiting.text}</p>
        ) : null}
      </div>
    </Panel>
  );
}
